import { useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { createTradeFair, updateTradeFair, type TradeFair } from '@/api/tradeFairs'
import { useApiErrorMessage } from '@/api/errors'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { DateField, type DateRange } from '@/components/layout/DateField'
import { Field } from '@/components/layout/Field'
import { FieldGrid } from '@/components/layout/FormSection'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

type Errors = { name?: string; startDate?: string; endDate?: string; city?: string }

/**
 * One dialog for creating and editing. The two take exactly the same fields — the backend's PUT
 * requires all of them, for the same reason — and a second component would only drift from this
 * one field by field.
 */
export function TradeFairDialog({
  open,
  fair,
  onOpenChange,
  onSaved,
}: {
  open: boolean
  /** Null creates a new fair; a fair edits that one. */
  fair: TradeFair | null
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {fair ? t('tradeFairs.form.editTitle') : t('tradeFairs.form.createTitle')}
          </DialogTitle>
          <DialogDescription>{t('tradeFairs.form.description')}</DialogDescription>
        </DialogHeader>
        {/* Keyed, so opening the dialog on another row mounts a fresh form instead of copying the
            new values over the old ones in an effect. */}
        {open && (
          <TradeFairForm
            key={fair?.id ?? 'new'}
            fair={fair}
            onOpenChange={onOpenChange}
            onSaved={onSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function TradeFairForm({
  fair,
  onOpenChange,
  onSaved,
}: {
  fair: TradeFair | null
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()

  const [name, setName] = useState(fair?.name ?? '')
  const [startDate, setStartDate] = useState(fair?.startDate ?? '')
  const [endDate, setEndDate] = useState(fair?.endDate ?? '')
  const [city, setCity] = useState(fair?.city ?? '')
  const [hall, setHall] = useState(fair?.hall ?? '')
  const [booth, setBooth] = useState(fair?.booth ?? '')
  const [notes, setNotes] = useState(fair?.notes ?? '')
  const [errors, setErrors] = useState<Errors>({})
  const [failure, setFailure] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  /**
   * Handed to both date fields, each adding which end it is: a click in either calendar then moves
   * the right one, and picking a whole period works from either side.
   */
  const period: Omit<DateRange, 'side'> = {
    start: startDate,
    end: endDate,
    onChange: (start, end) => {
      setStartDate(start)
      setEndDate(end)
    },
  }

  function validate(): boolean {
    const next: Errors = {}
    if (!name.trim()) {
      next.name = t('validation.required')
    }
    if (!city.trim()) {
      next.city = t('validation.required')
    }
    if (!startDate) {
      next.startDate = t('validation.required')
    }
    if (!endDate) {
      next.endDate = t('validation.required')
      // ISO dates compare correctly as strings, which is the one thing yyyy-mm-dd is good at.
    } else if (startDate && endDate < startDate) {
      // The backend refuses this too, and so does a check constraint. Saying it here saves a round
      // trip and puts the message next to the field that caused it.
      next.endDate = t('tradeFairs.form.endBeforeStart')
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitting || !validate()) {
      return
    }
    setSubmitting(true)
    setFailure(null)
    const body = {
      name: name.trim(),
      startDate,
      endDate,
      city: city.trim(),
      // Empty is absent, not an empty string: the backend trims to null, and sending "" would make
      // the field come back different from what was typed.
      hall: hall.trim() || null,
      booth: booth.trim() || null,
      notes: notes.trim() || null,
    }
    try {
      if (fair) {
        await updateTradeFair(fair.id, body)
        toast.success(t('tradeFairs.form.updated', { name: body.name }))
      } else {
        await createTradeFair(body)
        toast.success(t('tradeFairs.form.created', { name: body.name }))
      }
      onSaved()
      onOpenChange(false)
    } catch (error) {
      // TRADE_FAIR_DUPLICATE is the one worth reading, and it belongs in the dialog rather than in
      // a toast that outlives it.
      setFailure(errorMessage(error, t('tradeFairs.form.failed')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <div className="grid gap-4 py-2">
        {failure && (
          <Alert variant="destructive">
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}

        <Field
          id="fair-name"
          label={t('tradeFairs.form.name')}
          error={errors.name}
          hint={t('tradeFairs.form.nameHint')}
        >
          <Input
            id="fair-name"
            value={name}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? 'fair-name-error' : 'fair-name-hint'}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        {/* One period across two fields: either calendar picks both ends, because a fair is
            entered as "the week of the 10th" rather than as two separate dates. Typing into one
            field alone still works and leaves the other where it was. */}
        <FieldGrid>
          <Field id="fair-start" label={t('tradeFairs.form.startDate')} error={errors.startDate}>
            <DateField
              id="fair-start"
              value={startDate}
              invalid={Boolean(errors.startDate)}
              describedBy={errors.startDate ? 'fair-start-error' : undefined}
              calendarLabel={t('common.openCalendarFor', { field: t('tradeFairs.form.startDate') })}
              range={{ ...period, side: 'start' }}
              onChange={setStartDate}
            />
          </Field>
          <Field
            id="fair-end"
            label={t('tradeFairs.form.endDate')}
            error={errors.endDate}
            hint={t('tradeFairs.form.endDateHint')}
          >
            <DateField
              id="fair-end"
              value={endDate}
              // Not `min` alone: a browser that enforces it silently refuses the keystroke, and
              // the explicit message says why.
              min={startDate || undefined}
              invalid={Boolean(errors.endDate)}
              describedBy={errors.endDate ? 'fair-end-error' : 'fair-end-hint'}
              calendarLabel={t('common.openCalendarFor', { field: t('tradeFairs.form.endDate') })}
              range={{ ...period, side: 'end' }}
              onChange={setEndDate}
            />
          </Field>
        </FieldGrid>

        <Field id="fair-city" label={t('tradeFairs.form.city')} error={errors.city}>
          <Input
            id="fair-city"
            value={city}
            aria-invalid={Boolean(errors.city)}
            aria-describedby={errors.city ? 'fair-city-error' : undefined}
            onChange={(event) => setCity(event.target.value)}
          />
        </Field>

        <FieldGrid>
          <Field id="fair-hall" label={t('tradeFairs.form.hall')} hint={t('tradeFairs.form.standHint')}>
            <Input
              id="fair-hall"
              value={hall}
              aria-describedby="fair-hall-hint"
              onChange={(event) => setHall(event.target.value)}
            />
          </Field>
          <Field id="fair-booth" label={t('tradeFairs.form.booth')}>
            <Input
              id="fair-booth"
              value={booth}
              onChange={(event) => setBooth(event.target.value)}
            />
          </Field>
        </FieldGrid>

        <Field id="fair-notes" label={t('tradeFairs.form.notes')}>
          <Textarea
            id="fair-notes"
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={submitting}>
          {fair ? t('tradeFairs.form.save') : t('tradeFairs.form.create')}
        </Button>
      </DialogFooter>
    </form>
  )
}
